/**
 * The consent screens sit on `CenteredPage`'s dark terrain background, not on the app surface, so
 * the MUI light theme's text and divider tokens are all unreadable there. These are the handful of
 * overrides that let a design-system component render on the dark shell.
 *
 * The text colours are the theme's own `secondaryDarkBackground` ramp — the palette entry the
 * design system already keeps "for use on a dark background" — rather than hand-mixed whites.
 */

/**
 * Panel edges and rules. Deliberately quiet: the panel is meant to read as a pane of the shell
 * rather than as a bordered card, so the edge is a suggestion. The terrain behind it is
 * photographic, so the effective backdrop varies — measured here at 2.2:1, under the 3:1 WCAG
 * 1.4.11 floor for a non-text boundary, which is a considered trade rather than an oversight.
 */
const HAIRLINE = 'rgba(255, 255, 255, 0.22)';

export const onDark = {
  /**
   * See-through, and **darker** than the terrain rather than lighter. A light wash lifts the
   * backdrop, which costs the white text its contrast and reads as blurry; darkening it lets the
   * panel stay this transparent without giving up any contrast.
   */
  panel: {
    bgcolor: 'rgba(9, 21, 38, 0.3)',
    border: 1,
    borderColor: HAIRLINE,
    borderRadius: 1,
    color: 'common.white',
    // CenteredPage's content column sets a light font-weight for the login form. Reset it, so every
    // Typography inside renders at the weight its design-system variant specifies.
    fontWeight: 'fontWeightRegular',
    // Neither the legacy buttons nor the MUI checkbox draw a focus ring, and the shell is dark
    // enough that the UA default is easy to lose. This is a consent decision — the keyboard path
    // has to stay visible.
    '& :focus-visible': {
      outline: '2px solid',
      outlineColor: 'primaryDarkBackground.main',
      outlineOffset: 2,
    },
  },
  divider: { borderColor: HAIRLINE },
  /** Stands in for `text.secondary`, which is a dark grey. Measured at 12.9:1 on the panel. */
  secondaryText: { color: 'secondaryDarkBackground.main' },
  /**
   * A step below `secondaryText`, for marks and for our own words around the publisher-supplied
   * client name. The name renders at full white; this is the gap that sets the two apart, so it
   * has to be wide enough to see at a glance and still clear AA (6.7:1 on the panel).
   */
  mutedText: { color: 'secondaryDarkBackground.dark' },
  /**
   * The well that holds a string the *requesting app* supplied rather than one CMG asserts. Recessed
   * rather than bordered: the panel already carries a border, and a second one inside it reads as a
   * card. What matters is only that these strings never share a surface with our own copy.
   */
  claim: {
    bgcolor: 'rgba(0, 0, 0, 0.28)',
    borderRadius: 1,
    px: 1.5,
    py: 1.25,
  },
  /** For URIs and hosts: characters the user may need to compare one at a time. */
  mono: { fontFamily: 'Menlo, Consolas, monospace' },
} as const;

/**
 * Both buttons render exactly as the login screen's do, so consent and sign-in read as one flow —
 * the enabled colours are the shared components' own and are deliberately not overridden here.
 *
 * The cost is on the record: `variant="high-contrast"` is white on `#48B2FA` (2.33:1) and
 * `SecondaryButton` is the on-light `#007AC2` (3.6:1) — both under AA for 17px text. Fixing that
 * belongs in `@cmg/common`, where login and the error page would get it too; a local override here
 * would only make this one screen diverge from the rest of the shell.
 *
 * The disabled state is the exception. Its colours are mixed from the app surface's near-black, so
 * while a decision is in flight Cancel drops to roughly 1.5:1 and reads as broken rather than as
 * locked. Dim it against the shell instead.
 */
export const secondaryButtonOnDark = {
  '& button:disabled': {
    color: 'secondaryDarkBackground.dark',
    backgroundColor: 'transparent',
    borderColor: HAIRLINE,
  },
} as const;
